import { useEffect, useRef, useState } from "react";
import EntityManager from "@/components/EntityManager";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Printer, MessageCircle, Paperclip, BellRing, Trash2 } from "lucide-react";
import { api, API } from "@/lib/api";
import { fmtDate, today } from "@/lib/format";
import { printCircular, printCircularRegister } from "@/lib/print";
import { BRANCHES, loadBranches } from "@/lib/branches";
import { toast } from "sonner";

const CATEGORIES = ["General", "Diesel Price Hike", "Flood / Weather Alert", "Road Blocked / Diversion", "Festival / Holiday", "Rate Change", "Safety Instruction"];

const CATEGORY_STYLE = {
  "Diesel Price Hike": "bg-amber-100 text-amber-800",
  "Flood / Weather Alert": "bg-sky-100 text-sky-800",
  "Road Blocked / Diversion": "bg-red-100 text-red-700",
  "Festival / Holiday": "bg-emerald-100 text-emerald-700",
  "Rate Change": "bg-violet-100 text-violet-700",
  "Safety Instruction": "bg-orange-100 text-orange-700",
};

const isArchived = (r) => {
  const d = new Date(String(r.date || "").slice(0, 10));
  return !isNaN(d) && (Date.now() - d.getTime()) / 86400000 > 183;
};

const isPurgable = (r) => {
  const d = new Date(String(r.date || "").slice(0, 10));
  return !isNaN(d) && (Date.now() - d.getTime()) / 86400000 > 730;
};

const exportCircularCsv = (list) => {
  const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const lines = [["Sl No.", "Circular No.", "Date", "Subject", "Category", "To Branch", "Issued By", "Attachment", "Read By"].join(",")];
  list.forEach((r, i) =>
    lines.push([
      i + 1,
      String(r.circular_no || "").replace(/^CIR[-\s]?/i, "CIR-"),
      r.date || "",
      r.subject || "",
      r.category || "General",
      r.to_branch || "All Branches",
      r.issued_by || "",
      r.attachment_filename || "",
      (r.acks || []).map((a) => `${a.name}${a.office ? ` (${a.office})` : ""}`).join("; "),
    ].map(cell).join(","))
  );
  const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `NTC-Circular-Register-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

export default function Circulars() {
  const [branchList, setBranchList] = useState(["All Branches"]);
  const [contacts, setContacts] = useState({});
  const [reloadKey, setReloadKey] = useState(0);
  const [purgeList, setPurgeList] = useState(null);
  const doPurge = async () => {
    const list = purgeList || [];
    setPurgeList(null);
    let ok = 0;
    for (const c of list) {
      try {
        await api.delete(`/circulars/${c.id}`);
        ok += 1;
      } catch {
        /* skip */
      }
    }
    toast.success(`${ok} archived circular(s) older than 2 years purged`);
    setReloadKey((k) => k + 1);
  };
  const fileRef = useRef(null);
  const attachTargetRef = useRef(null);
  const token = localStorage.getItem("ntc_token");
  useEffect(() => {
    loadBranches().then(() => setBranchList(["All Branches", ...BRANCHES]));
    api.get("/branch-contacts").then((r) => setContacts(r.data || {})).catch(() => {});
  }, []);
  const pickAttachment = (c) => {
    attachTargetRef.current = c;
    fileRef.current?.click();
  };
  const uploadAttachment = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const c = attachTargetRef.current;
    if (!file || !c) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/circulars/${c.id}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Attachment "${file.name}" added to ${String(c.circular_no || "").replace(/^CIR[-\s]?/i, "CIR-")}`);
      setReloadKey((k) => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Attachment upload failed");
    }
  };
  const unreadBranches = (c) => {
    const ackOffices = new Set((c.acks || []).map((a) => a.office).filter(Boolean));
    const targets = !c.to_branch || c.to_branch === "All Branches" ? BRANCHES : [c.to_branch];
    return targets.filter((b) => !ackOffices.has(b));
  };
  const daysOld = (c) => Math.floor((new Date(today()) - new Date(String(c.date || "").slice(0, 10) || today())) / 86400000);
  const chaseWa = (c) => {
    const pending = unreadBranches(c);
    const msg = [
      "*NTC — Circular Reminder*",
      `Circular ${String(c.circular_no || "").replace(/^CIR[-\s]?/i, "CIR-")} dated ${c.date ? fmtDate(c.date) : "—"} is still unread by your branch (${daysOld(c)} days).`,
      `*Subject: ${c.subject || ""}*`,
      "Please open the NTC dashboard, read the circular and tap Mark as Read.",
      "— Head Office, Nisha Transport Corporation",
    ].join("\n");
    let num = pending.length === 1 ? String(contacts[pending[0]] || "").replace(/\D/g, "") : "";
    if (num.length === 10) num = `91${num}`;
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const shareWa = (c) => {
    const msg = [
      `*NTC CIRCULAR ${String(c.circular_no || "").replace(/^CIR[-\s]?/i, "CIR-")}* · ${c.date ? fmtDate(c.date) : ""}`,
      `To: ${c.to_branch || "All Branches"}`,
      `Category: ${c.category || "General"}`,
      `*Subject: ${c.subject || ""}*`,
      "",
      c.message || "",
      "",
      "— Nisha Transport Corporation (Head Office)",
    ].join("\n");
    let num = c.to_branch && c.to_branch !== "All Branches" ? String(contacts[c.to_branch] || "").replace(/\D/g, "") : "";
    if (num.length === 10) num = `91${num}`;
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  return (
    <>
    <input type="file" ref={fileRef} className="hidden" accept=".jpg,.jpeg,.png,.webp,.pdf" onChange={uploadAttachment} data-testid="circular-attachment-input" />
    <Dialog open={!!purgeList} onOpenChange={(o) => !o && setPurgeList(null)}>
      <DialogContent className="sm:max-w-[480px]" data-testid="circular-purge-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Auto-Clean Old Circulars</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {(purgeList || []).length} archived circular(s) older than <b>2 years</b> will be permanently removed (they go to the Recycle Bin first):
        </p>
        <div className="max-h-44 overflow-y-auto rounded-md border border-border p-2 text-xs">
          {(purgeList || []).map((c) => (
            <div key={c.id} className="flex items-center gap-2 py-0.5" data-testid="purge-row">
              <span className="font-bold">{String(c.circular_no || "—").replace(/^CIR[-\s]?/i, "CIR-")}</span>
              <span>{c.date ? fmtDate(c.date) : "—"}</span>
              <span className="truncate text-muted-foreground">{c.subject}</span>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setPurgeList(null)}>Cancel</Button>
          <Button variant="destructive" onClick={doPurge} data-testid="purge-confirm-btn">
            <Trash2 className="mr-2 h-4 w-4" /> Purge {(purgeList || []).length} Circular(s)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <EntityManager
      key={reloadKey}
      title="NTC Circulars"
      subtitle="Circulate updates to branches — diesel price hikes, floods, road blocks, festivals and more."
      endpoint="circulars"
      entityName="Circular"
      pageSize={20}
      selectable
      onPrintSelected={(list) => printCircularRegister(list)}
      exportCsv={exportCircularCsv}
      headerExtra={(rows) => {
        const old = (rows || []).filter(isPurgable);
        return old.length ? (
          <Button
            variant="outline"
            className="border-red-300 bg-red-50 font-semibold text-red-700 hover:bg-red-100"
            onClick={() => setPurgeList(old)}
            data-testid="circular-purge-btn"
          >
            <Trash2 className="mr-2 h-4 w-4" /> Auto-Clean ({old.length}) 2yr+
          </Button>
        ) : null;
      }}
      defaults={{
        circular_no: "",
        date: today(),
        subject: "",
        category: "General",
        to_branch: "All Branches",
        issued_by: "",
        message: "",
      }}
      fields={[
        { key: "circular_no", label: "Circular No. (leave blank for auto)", type: "text", placeholder: "e.g. CIR-1005 or HO/2026/12" },
        { key: "date", label: "Circular Date", type: "date" },
        { key: "subject", label: "Subject", type: "text", placeholder: "Diesel price hike w.e.f. 01 Oct 2026" },
        { key: "category", label: "Category", type: "text", placeholder: "General, Diesel Price Hike, Festival / Holiday…" },
        { key: "to_branch", label: "To Branch", type: "select", options: branchList },
        { key: "issued_by", label: "Circular Issued By", type: "text", placeholder: "e.g. Managing Director / HO Admin" },
        { key: "message", label: "Circular Message", type: "textarea", placeholder: "Details of the update / instructions for branches…" },
      ]}
      columns={[
        { key: "circular_no", label: "Circular No.", render: (r) => String(r.circular_no || "—").replace(/^CIR[-\s]?/i, "CIR-") },
        { key: "date", label: "Date", render: (r) => (
          <span className="whitespace-nowrap">
            {r.date ? fmtDate(r.date) : "—"}
            {isArchived(r) && <span className="ml-1.5 rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-600" data-testid="circular-archived-chip">Archived</span>}
          </span>
        ) },
        { key: "subject", label: "Subject", render: (r) => <span className="font-semibold">{r.subject || "—"}</span> },
        {
          key: "category",
          label: "Category",
          render: (r) => (
            <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold ${CATEGORY_STYLE[r.category] || "bg-slate-100 text-slate-700"}`} data-testid="circular-category-chip">
              {r.category || "General"}
            </span>
          ),
        },
        { key: "to_branch", label: "To", render: (r) => r.to_branch || "All Branches" },
        {
          key: "attachment",
          label: "Attachment",
          render: (r) =>
            r.attachment_path ? (
              <a
                href={`${API}/files/${r.attachment_path}?auth=${token}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex max-w-[160px] items-center gap-1 truncate text-xs font-semibold text-sky-700 hover:underline"
                title={r.attachment_filename}
                data-testid="circular-attachment-link"
              >
                <Paperclip className="h-3 w-3 shrink-0" />
                <span className="truncate">{r.attachment_filename || "Attachment"}</span>
              </a>
            ) : (
              <span className="text-xs text-muted-foreground">—</span>
            ),
        },
        { key: "issued_by", label: "Issued By", render: (r) => r.issued_by || "—" },
        {
          key: "acks",
          label: "Read By",
          render: (r) => {
            const acks = r.acks || [];
            if (!acks.length) return <span className="text-xs text-muted-foreground">—</span>;
            const label = acks.map((a) => `${a.name}${a.office ? ` (${a.office})` : ""}`).join(", ");
            return (
              <span className="inline-flex items-center gap-1 text-xs" title={label} data-testid="circular-read-by">
                <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">{acks.length} ✓</span>
                <span className="max-w-[180px] truncate text-slate-600">{label}</span>
              </span>
            );
          },
        },
      ]}
      filters={[
        { key: "q", label: "Search Subject", type: "text", placeholder: "e.g. diesel, route, festival…", match: (r, v) => !v || `${r.subject || ""} ${r.circular_no || ""} ${r.message || ""}`.toLowerCase().includes(String(v).toLowerCase()) },
        { key: "view", label: "View", type: "select", options: ["Active", "Archived (6+ months)", "All"], match: (r, v, all) => (all?.q ? true : v === "All" ? true : v === "Archived (6+ months)" ? isArchived(r) : !isArchived(r)) },
        { key: "cat", label: "Category", type: "select", options: ["All Categories", ...CATEGORIES], match: (r, v) => !v || v === "All Categories" || r.category === v },
      ]}
      rowActions={(r) => (
        <>
          {daysOld(r) >= 2 && unreadBranches(r).length > 0 && (
            <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-600" title={`Unread ${daysOld(r)}d by: ${unreadBranches(r).join(", ")} — send WhatsApp reminder`} onClick={() => chaseWa(r)} data-testid="circular-chase-btn">
              <BellRing className="h-4 w-4" />
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-600" title="Attach photo / PDF (rate chart, route map)" onClick={() => pickAttachment(r)} data-testid="circular-attach-btn">
            <Paperclip className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600" title="Send circular to branch on WhatsApp" onClick={() => shareWa(r)} data-testid="circular-wa-btn">
            <MessageCircle className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-primary" title="Print circular" onClick={() => printCircular(r)} data-testid="circular-print-btn">
            <Printer className="h-4 w-4" />
          </Button>
        </>
      )}
    />
    </>
  );
}
